// ═══════════════════════════════════════════════════════════════
// RelationshipsPanel
// Path: src/components/donorDetail/RelationshipsPanel.jsx
//
// Displays and manages member relationships (spouse, child, parent,
// employee, board_contact, other). Used in DonorDetailSidebar and
// DonorDetailClassic.
//
// Props-only — does not call useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from "react";
import { Users, Plus, X, ArrowUpRight, UserCheck } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { RELATIONSHIP_TYPES } from "../../constants/memberTypes";
import { relationshipService } from "../../services/relationshipService";
import { ActionButton } from "../ui/ActionButton";
import { Badge } from "../ui/Badge";

// ═══════════════════════════════════════════════════════════════
// Relationship type colors
// ═══════════════════════════════════════════════════════════════

const TYPE_COLORS = {
  spouse: "#8b5cf6",
  child: "#3b82f6",
  parent: "#0ea5e9",
  employee: "#f59e0b",
  board_contact: "#10b981",
  other: "#6b7280",
};

// ═══════════════════════════════════════════════════════════════
// Add Relationship Form (inline)
// ═══════════════════════════════════════════════════════════════

const AddRelationshipForm = ({
  members,
  selectedMemberId,
  palette,
  onAdd,
  onCancel,
}) => {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [relType, setRelType] = useState("spouse");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredMembers = useMemo(() => {
    if (!search.trim()) return [];
    const query = search.toLowerCase();
    return (members || [])
      .filter(
        (m) =>
          m.id !== selectedMemberId &&
          `${m.firstName} ${m.lastName} ${m.organization}`
            .toLowerCase()
            .includes(query)
      )
      .slice(0, 10);
  }, [search, members, selectedMemberId]);

  const handleSubmit = async () => {
    if (!selectedId || !relType) return;
    setSaving(true);
    try {
      await onAdd(selectedId, relType, notes);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    padding: "7px 10px",
    borderRadius: 6,
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
    width: "100%",
  };

  return (
    <div
      style={{
        padding: "10px 12px",
        background: `${palette.accent1}06`,
        borderRadius: 8,
        border: `1px solid ${palette.cardBorder}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Member search */}
      <div style={{ position: "relative" }}>
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelectedId("");
          }}
          placeholder="Search for a member..."
          style={inputStyle}
          autoFocus
        />
        {filteredMembers.length > 0 && !selectedId && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 10,
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            {filteredMembers.map((m) => (
              <div
                key={m.id}
                onClick={() => {
                  setSelectedId(m.id);
                  setSearch(`${m.firstName} ${m.lastName}`);
                }}
                style={{
                  padding: "6px 10px",
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                  color: palette.text1,
                  cursor: "pointer",
                  borderBottom: `1px solid ${palette.cardBorder}`,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = `${palette.accent1}08`)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <strong>
                  {m.lastName}, {m.firstName}
                </strong>
                {m.organization && (
                  <span style={{ color: palette.text3, marginLeft: 6 }}>
                    {m.organization}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Relationship type */}
      <select
        value={relType}
        onChange={(e) => setRelType(e.target.value)}
        style={{ ...inputStyle, cursor: "pointer" }}
      >
        {RELATIONSHIP_TYPES.map((rt) => (
          <option key={rt.value} value={rt.value}>
            {rt.label}
          </option>
        ))}
      </select>

      {/* Notes */}
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        style={inputStyle}
      />

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button
          onClick={onCancel}
          style={{
            padding: "5px 12px",
            borderRadius: 6,
            border: `1px solid ${palette.cardBorder}`,
            background: "transparent",
            color: palette.text2,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!selectedId || saving}
          style={{
            padding: "5px 12px",
            borderRadius: 6,
            border: "none",
            background: selectedId ? palette.accent1 : palette.inputBorder,
            color: "#fff",
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            cursor: selectedId ? "pointer" : "default",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving..." : "Add"}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// RelationshipsPanel
// ═══════════════════════════════════════════════════════════════

export const RelationshipsPanel = ({
  selectedMember,
  members,
  canEdit,
  palette,
  navigate,
  showMessage,
}) => {
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // Load relationships when member changes
  useEffect(() => {
    if (!selectedMember?.id) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const rels = await relationshipService.getByMember(selectedMember.id);
        if (!cancelled) setRelationships(rels);
      } catch (err) {
        // Silent — relationships are optional
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedMember?.id]);

  const handleAdd = async (relatedMemberId, relType, notes) => {
    try {
      await relationshipService.create(
        selectedMember.id,
        relatedMemberId,
        relType,
        notes
      );
      // Reload relationships
      const rels = await relationshipService.getByMember(selectedMember.id);
      setRelationships(rels);
      setShowAdd(false);
      if (showMessage) showMessage("Relationship added");
    } catch (err) {
      if (showMessage)
        showMessage("Failed to add relationship: " + err.message, "error");
    }
  };

  const handleRemove = async (relId) => {
    try {
      await relationshipService.remove(relId);
      setRelationships((prev) => prev.filter((r) => r.id !== relId));
      if (showMessage) showMessage("Relationship removed");
    } catch (err) {
      if (showMessage)
        showMessage("Failed to remove relationship: " + err.message, "error");
    }
  };

  const handlePromote = async () => {
    try {
      // Find parent relationships for this member
      const parentRels = relationships.filter(
        (r) => r.relationshipType === "parent"
      );
      // Remove parent relationships
      for (const rel of parentRels) {
        await relationshipService.remove(rel.id);
      }
      // Reload
      const rels = await relationshipService.getByMember(selectedMember.id);
      setRelationships(rels);
      if (showMessage)
        showMessage(
          `${selectedMember.firstName} promoted to individual donor`
        );
    } catch (err) {
      if (showMessage)
        showMessage("Failed to promote: " + err.message, "error");
    }
  };

  const hasParentRelationship = relationships.some(
    (r) => r.relationshipType === "parent"
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <Users size={ICON_SIZES.xs} />
          Relationships
          {relationships.length > 0 && (
            <span
              style={{
                background: palette.accent1,
                color: "#fff",
                borderRadius: 10,
                padding: "0 6px",
                fontSize: 10,
                fontWeight: 700,
                marginLeft: 2,
              }}
            >
              {relationships.length}
            </span>
          )}
        </div>
        {canEdit && !showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            title="Add relationship"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 8px",
              borderRadius: 6,
              border: "none",
              background: `${palette.accent1}12`,
              color: palette.accent1,
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus size={ICON_SIZES.xs} /> Add
          </button>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <AddRelationshipForm
          members={members}
          selectedMemberId={selectedMember?.id}
          palette={palette}
          onAdd={handleAdd}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {/* Relationship list */}
      {loading ? (
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
            fontStyle: "italic",
            padding: "4px 0",
          }}
        >
          Loading...
        </div>
      ) : relationships.length === 0 && !showAdd ? (
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
            padding: "4px 0",
          }}
        >
          No relationships
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {relationships.map((rel) => {
            const rm = rel.relatedMember;
            const typeLabel =
              RELATIONSHIP_TYPES.find((t) => t.value === rel.relationshipType)
                ?.label || rel.relationshipType;
            const typeColor =
              TYPE_COLORS[rel.relationshipType] || TYPE_COLORS.other;

            return (
              <div
                key={rel.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: 6,
                  background: `${typeColor}06`,
                  border: `1px solid ${typeColor}15`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text1,
                        cursor: navigate ? "pointer" : "default",
                        textDecoration: navigate ? "none" : "none",
                      }}
                      onClick={() => {
                        if (navigate && rm) navigate("memberDetail", rm);
                      }}
                      onMouseEnter={(e) => {
                        if (navigate)
                          e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {rm
                        ? `${rm.firstName} ${rm.lastName}`
                        : "Unknown member"}
                    </span>
                    <Badge
                      label={typeLabel}
                      color={typeColor}
                      small
                    />
                  </div>
                  {rel.notes && (
                    <div
                      style={{
                        fontSize: 11,
                        color: palette.text3,
                        marginTop: 2,
                      }}
                    >
                      {rel.notes}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <button
                    onClick={() => handleRemove(rel.id)}
                    title="Remove relationship"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      border: "none",
                      background: "transparent",
                      color: palette.text3,
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.color = "#dc2626")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.color = palette.text3)
                    }
                  >
                    <X size={ICON_SIZES.xs} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Promote to Individual button */}
      {canEdit && hasParentRelationship && (
        <button
          onClick={handlePromote}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 6,
            border: `1px solid ${palette.accent1}30`,
            background: `${palette.accent1}08`,
            color: palette.accent1,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            cursor: "pointer",
            marginTop: 4,
          }}
        >
          <UserCheck size={ICON_SIZES.sm} />
          Promote to Individual Donor
        </button>
      )}
    </div>
  );
};
